import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { Skeleton } from '~/components/ui/skeleton';
import { useECharts } from '~/hooks/use-echarts';
import { resolveThemeColor } from '~/utils/chart-colors';
import { cn } from '~/utils/cn';

import type { WeeklyDidRevenuePoint } from '../../types/experiment.types';
import { formatPercentFromDecimal } from '../../utils/format-percent';

interface Props {
  points: WeeklyDidRevenuePoint[];
  isLoading?: boolean | undefined;
  className?: string | undefined;
}

/**
 * "DiD % Revenue — Week by Week" line chart for the category-mode Performance
 * tab. Single-series line; area + line are colored green when above zero and
 * red when below via `visualMap.piecewise`. A dashed `markLine` at y=0
 * provides a visual zero baseline.
 */
export function ExperimentWeeklyDidRevenueChart({ points, isLoading, className }: Props) {
  const option = useMemo<EChartsOption>(() => {
    const successColor = resolveThemeColor('--success');
    const errorColor = resolveThemeColor('--error');
    const axisColor = resolveThemeColor('--muted-foreground');
    const gridLineColor = resolveThemeColor('--border');

    const xAxisCategories = points.map((p) => p.weekLabel);
    const data = points.map((p) => p.didPct);

    return {
      grid: { left: 16, right: 16, top: 24, bottom: 32, containLabel: true },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (val: unknown) => (typeof val === 'number' ? formatPct(val) : String(val)),
      },
      visualMap: {
        show: false,
        dimension: 1,
        pieces: [
          { gt: 0, color: successColor },
          { lte: 0, color: errorColor },
        ],
      },
      xAxis: {
        type: 'category',
        data: xAxisCategories,
        boundaryGap: false,
        axisLine: { lineStyle: { color: gridLineColor } },
        axisTick: { show: false },
        axisLabel: { color: axisColor, fontSize: 12 },
      },
      yAxis: {
        type: 'value',
        position: 'left',
        axisLabel: { color: axisColor, fontSize: 12, formatter: formatPct },
        splitLine: { lineStyle: { color: gridLineColor, type: 'dashed' } },
      },
      series: [
        {
          type: 'line',
          data,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          lineStyle: { width: 2 },
          areaStyle: { opacity: 0.15 },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: gridLineColor, type: 'dashed', width: 1 },
            data: [{ yAxis: 0 }],
          },
        },
      ],
    } as EChartsOption;
  }, [points]);

  const chartRef = useECharts(option);

  return (
    <div className={cn('relative h-64 w-full min-w-0', className)}>
      <div ref={chartRef} className="absolute inset-0" />
      {isLoading ? <Skeleton className="absolute inset-0" /> : null}
    </div>
  );
}

// Always-1-dp so the tooltip preserves the direction signal for sub-1%
// values; otherwise the `visualMap` paints the area red below zero while
// the tooltip reads "0%" — a contradiction the viewer can't reconcile.
const formatPct = (value: number) => formatPercentFromDecimal(value, { precision: 'chart-1dp' });
